const SESSION_KEY = 'maestro_current_session';
const SESSIONS_KEY = 'maestro_sessions';
const MESSAGES_PREFIX = 'maestro_messages_';
const DELETED_SESSIONS_KEY = 'maestro_deleted_sessions';

export const sessionService = {
  getCurrentSession() {
    return localStorage.getItem(SESSION_KEY);
  },

  setCurrentSession(sessionId) {
    localStorage.setItem(SESSION_KEY, sessionId);
  },

  addSessionToList(session) {
    const sessions = this.getSessionsList();
    const sessionId = session.session_id || session.id;
    
    // Check if session already exists to prevent duplicates
    const existingIndex = sessions.findIndex(s => (s.id === sessionId || s.session_id === sessionId));
    if (existingIndex >= 0) {
      // Session already exists, update it instead of creating duplicate
      if (session.title) {
        sessions[existingIndex].title = session.title;
      }
      sessions[existingIndex].updatedAt = new Date().toISOString();
    } else {
      // New session, add it
      sessions.unshift({
        id: sessionId,
        session_id: sessionId,  // Explicitly store session_id for clarity
        title: session.title || this.generateTitle(session),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  },

  getSessionsList() {
    const data = localStorage.getItem(SESSIONS_KEY);
    return data ? JSON.parse(data) : [];
  },

  generateTitle(session) {
    // Generate title from first message
    return session.messages?.[0]?.content?.substring(0, 30) || 'New Chat';
  },

  getMessages(sessionId) {
    const data = localStorage.getItem(MESSAGES_PREFIX + sessionId);
    return data ? JSON.parse(data) : [];
  },

  saveMessages(sessionId, messages) {
    localStorage.setItem(MESSAGES_PREFIX + sessionId, JSON.stringify(messages || []));
    const sessions = this.getSessionsList();
    const idx = sessions.findIndex(s => (s.id === sessionId || s.session_id === sessionId));
    if (idx >= 0) {
      sessions[idx].updatedAt = new Date().toISOString();
      
      // Update title if it's still "New Chat" or empty, and we have messages
      if (messages && messages.length > 0) {
        const firstUserMessage = messages.find(msg => msg.role === 'user');
        if (firstUserMessage && (!sessions[idx].title || sessions[idx].title === 'New Chat' || sessions[idx].title === 'Chat')) {
          // Generate title from first user message (truncate to 50 chars for readability)
          const title = firstUserMessage.content.substring(0, 50).trim();
          sessions[idx].title = title || 'New Chat';
        }
      }
      
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    } else {
      // Session has messages but no thread entry, create one
      if (messages && messages.length > 0) {
        const firstUserMessage = messages.find(msg => msg.role === 'user');
        const title = firstUserMessage ? firstUserMessage.content.substring(0, 50).trim() : 'New Chat';
        this.addSessionToList({ 
          session_id: sessionId, 
          messages: messages,
          title: title
        });
      }
    }
  },

  deleteSession(sessionId) {
    // Remove session from list (check both id and session_id fields)
    const sessions = this.getSessionsList();
    const filtered = sessions.filter(s => (s.id !== sessionId && s.session_id !== sessionId));
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(filtered));
    
    // Remove messages
    localStorage.removeItem(MESSAGES_PREFIX + sessionId);
    
    // If this was the current session, clear it
    const currentSession = this.getCurrentSession();
    if (currentSession === sessionId) {
      localStorage.removeItem(SESSION_KEY);
    }
    // Track deleted session IDs to prevent rehydration from backend
    const deleted = this.getDeletedSessions();
    if (!deleted.includes(sessionId)) {
      deleted.push(sessionId);
      localStorage.setItem(DELETED_SESSIONS_KEY, JSON.stringify(deleted));
    }
    
    return filtered;
  },

  getDeletedSessions() {
    const data = localStorage.getItem(DELETED_SESSIONS_KEY);
    return data ? JSON.parse(data) : [];
  },

  clearDeletedSessions() {
    localStorage.removeItem(DELETED_SESSIONS_KEY);
  },

  deleteAllSessions() {
    // Get all session IDs first
    const sessions = this.getSessionsList();
    const sessionIds = sessions.map(s => s.id);
    
    // Remove all messages
    sessionIds.forEach(sessionId => {
      localStorage.removeItem(MESSAGES_PREFIX + sessionId);
    });
    
    // Clear sessions list and current session
    localStorage.removeItem(SESSIONS_KEY);
    localStorage.removeItem(SESSION_KEY);
    
    return [];
  }
};

